import React from 'react'

import { twMerge } from 'tailwind-merge'

import type { Variant } from '@/types/components/Variant'

interface Spinner {
  className?: string
  variant?: Variant
}

const Spinner: React.FC<Spinner> = ({ className = '', variant }) => {
  let spinnerTailwindClasses: string
  switch (variant) {
    case 'danger':
      spinnerTailwindClasses =
        'fill-red-700 america:fill-red-700 dark:fill-red-700 text-red-200 america:!text-red-200 dark:text-red-200'
      break
    case 'danger-compact':
      spinnerTailwindClasses =
        'fill-red-700 america:fill-red-700 dark:fill-red-700 text-red-200 america:!text-red-200 dark:text-red-200'
      break
    case 'outline':
      spinnerTailwindClasses =
        'fill-primary-600 america:fill-primary-600 dark:fill-primary-400 text-primary-200 dark:text-primary-800'
      break
    case 'outline-compact':
      spinnerTailwindClasses =
        'fill-primary-600 america:fill-primary-600 dark:fill-primary-400 text-primary-200 dark:text-primary-800'
      break
    case 'soft':
      spinnerTailwindClasses =
        'fill-primary-600 america:fill-primary-600 dark:fill-primary-400 text-primary-200 dark:text-primary-800'
      break
    case 'soft-compact':
      spinnerTailwindClasses =
        'fill-primary-600 america:fill-primary-600 dark:fill-primary-400 text-primary-200 dark:text-primary-800'
      break
    // the professional theme paints a solid button `accent-600`, which is what
    // the track was — see `Button`, whose copy of these strings this matches
    case 'primary':
      spinnerTailwindClasses =
        'fill-white america:fill-white professional:fill-white dark:fill-primary-300 text-accent-600 professional:text-accent-400 dark:text-accent-600'
      break
    case 'primary-compact':
      spinnerTailwindClasses =
        'fill-white america:fill-white professional:fill-white dark:fill-primary-100 text-accent-600 professional:text-accent-400 dark:text-accent-400/60'
      break
    case 'positive':
      spinnerTailwindClasses =
        'fill-green-100 america:fill-green-100 dark:fill-green-100 text-green-700 america:!text-green-700 dark:text-green-700'
      break
    case 'positive-compact':
      spinnerTailwindClasses =
        'fill-green-100 america:fill-green-100 dark:fill-green-300 text-green-700 america:!text-green-700 dark:text-green-700'
      break
    case 'blue':
      spinnerTailwindClasses =
        'fill-blue-100 america:fill-blue-100 dark:fill-blue-300 text-blue-700 america:!text-blue-700 dark:text-blue-700'
      break
    case 'blue-compact':
      spinnerTailwindClasses =
        'fill-blue-100 america:fill-blue-100 dark:fill-blue-300 text-blue-700 america:!text-blue-700 dark:text-blue-700'
      break
    case 'amber':
      spinnerTailwindClasses =
        'fill-amber-100 america:fill-amber-100 dark:fill-amber-300 text-amber-700 america:!text-amber-700 dark:text-amber-700'
      break
    case 'amber-compact':
      spinnerTailwindClasses =
        'fill-amber-100 america:fill-amber-100 dark:fill-amber-300 text-amber-700 america:!text-amber-700 dark:text-amber-700'
      break
    default:
      spinnerTailwindClasses =
        'text-primary-300 dark:text-primary-600 fill-accent-400 america:fill-primary-600 professional:fill-accent-600'
  }

  return (
    <div role="status">
      <svg
        aria-hidden="true"
        className={twMerge(
          'w-6 h-6 motion-safe:animate-spin',
          spinnerTailwindClasses,
          className,
        )}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export const FullScreenSpinner: React.FC = () => {
  return (
    <div className="flex items-center justify-center h-screen w-screen overflow-hidden">
      <Spinner />
    </div>
  )
}

export const FullContainerSpinner: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <div className="flex items-center justify-center h-full w-full overflow-hidden gap-3">
      <Spinner />
      {children}
    </div>
  )
}

export default Spinner
